/**
 * ContractManager.js  — AMOS 계약서 관리 (리팩토링)
 *
 * 변경 사항:
 *   - CropTool         → imageUtils.js 공통 컴포넌트 사용
 *   - 이미지 뷰어 모달 → ImageViewer 컴포넌트 사용
 *   - 이미지 로드 로직 → useImageCache 훅 사용
 *   - 앞면/뒷면 업로드 박스 → ImageUploadBox 컴포넌트 사용
 *   - 기능·API 변경 없음
 */

import { getLabels } from '../fieldLabels';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CropTool, ImageViewer, ImageUploadBox, useImageCache } from './imageUtils';
import { sendWelcomeKakao, tokenApi } from './bleUtils';


// ── PDF 다운로드 (다중 페이지) ───────────────────────────────
async function loadJsPdf() {
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return window.jspdf.jsPDF;
}

async function downloadAllPdf(imgDataList, filename) {
  const validImgs = imgDataList.filter(Boolean);
  if (!validImgs.length) { alert('이미지가 없습니다'); return; }
  const jsPDF = await loadJsPdf();
  let pdf = null;
  for (const imgData of validImgs) {
    const img = new Image();
    img.src = imgData;
    await new Promise(r => { img.onload = r; });
    const isLandscape = img.width > img.height;
    if (!pdf) {
      pdf = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'px', format: [img.width, img.height] });
    } else {
      pdf.addPage([img.width, img.height], isLandscape ? 'landscape' : 'portrait');
    }
    pdf.addImage(imgData, 'WEBP', 0, 0, img.width, img.height);
  }
  pdf.save(filename + '.pdf');
}

const API_BASE = 'https://adria.terrier-ule.ts.net';



const S = {
  page:   { padding: '10px', fontFamily: '"Pretendard", sans-serif', backgroundColor: '#f3f4f6', minHeight: '100vh', maxWidth: '500px', margin: '0 auto' },
  card:   { background: '#fff', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', marginBottom: '12px', overflow: 'hidden' },
  header: { padding: '14px 15px', borderBottom: '1px solid #f3f4f6', fontWeight: 'bold', fontSize: '14px', color: '#1f2937' },
  row:    { padding: '12px 15px', borderBottom: '1px solid #f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label:  { fontSize: '11px', color: '#6b7280', marginBottom: '4px' },
  input:  { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box', outline: 'none' },
  btn:    (color) => ({ padding: '5px 10px', backgroundColor: color, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }),
};


export default function ContractManager({ token, tenantId, template }) {
  const L = getLabels(template);
  const [contracts,     setContracts]     = useState(() => {
    try { return JSON.parse(localStorage.getItem(`contracts_cache_${tenantId}`) || '[]'); }
    catch { return []; }
  });
  const [loading,       setLoading]       = useState(false);
  const [filterType,    setFilterType]    = useState('all'); // 'all' | 'inactive' | '1'~'6' (층)
  const [showUpload,    setShowUpload]    = useState(false);
  const [contractMode,  setContractMode]  = useState('edit');
  const [viewer,        setViewer]        = useState(null);
  const [viewerTab,     setViewerTab]     = useState('0');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editTarget,    setEditTarget]    = useState(null);
  const [form,          setForm]          = useState({ room_no: '', user_name: '', pages: [] });
  const [uploading,     setUploading]     = useState(false);
  const [error,         setError]         = useState('');
  const [sending,       setSending]       = useState(null);
  const [success,       setSuccess]       = useState('');
  const [rooms,         setRooms]         = useState(() => {
    try { return JSON.parse(localStorage.getItem(`rooms_cache_${tenantId}`) || '[]'); }
    catch { return []; }
  });
  const [cropTarget,    setCropTarget]    = useState(null);
  const [pagePreviews, setPagePreviews] = useState([]);
  const [pageCrops,    setPageCrops]    = useState([]);
  const [pageFilenames, setPageFilenames] = useState([]); // 기존 파일명(null이면 새 파일)
  const [cropIdx,      setCropIdx]      = useState(null);
  const dragItemRef = useRef(null);
  const dragOverRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragY,   setDragY]   = useState(0);
  const dragStartYRef = useRef(0);
  const ITEM_H = 46;

  const headers = { Authorization: `Bearer ${token}` };

  // ── 이미지 캐시 훅 사용 ─────────────────────────────────────
  const { cache: imgCache, loading: imgLoading, fetchImage, clearCache } = useImageCache(
    useCallback(async (filename) => {
      const res  = await fetch(`${API_BASE}/api/contracts/image/${tenantId}/${encodeURIComponent(filename)}`, { headers });
      const data = await res.json();
      return data.data || null;
    }, [tenantId])
  );

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/contracts`, { headers });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setContracts(list);
      try { localStorage.setItem(`contracts_cache_${tenantId}`, JSON.stringify(list)); } catch {}
    } catch { setError('목록 로드 실패'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchContracts();
    fetch(`${API_BASE}/api/rooms?include_inactive=true`, { headers })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setRooms(list);
        try { localStorage.setItem(`rooms_cache_${tenantId}`, JSON.stringify(list)); } catch {}
      });
  }, []);

  const openViewer = async (c) => {
    let roomContracts = [c];
    try {
      const res = await fetch(`${API_BASE}/api/contracts?room_no=${encodeURIComponent(c.room_no)}`, { headers });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0)
        roomContracts = data
          .filter(x => x.user_name === c.user_name)
          .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    } catch {}
    // 각 계약서의 pages 배열 별도 조회
    roomContracts = await Promise.all(roomContracts.map(async rc => {
      try {
        const r = await fetch(`${API_BASE}/api/contracts/${rc.id}/pages`, { headers });
        const d = await r.json();
        return { ...rc, pages: d.pages };
      } catch { return rc; }
    }));
    setViewer({ ...c, roomContracts });
    setViewerTab('0');
    // 모든 페이지 이미지 강제 새로고침 (캐시 무효화 후 재로드)
    const allPaths = roomContracts.flatMap(rc => {
      const ps = rc.pages || ([rc.front_path].concat(rc.back_path ? [rc.back_path] : []));
      return ps.filter(Boolean);
    });
    if (clearCache) clearCache(allPaths);
    allPaths.forEach(p => fetchImage(p));
  };

  const handleUpload = async () => {
    const isEdit = !!editTarget;
    if (!form.room_no || !form.user_name || !form.pages.length) { setError('호실, 이름, 이미지 최소 1장 필수입니다'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('room_no',    form.room_no);
      fd.append('user_name',  form.user_name);
      form.pages.filter(p => p instanceof File).forEach(p => fd.append('pages', p));
      const newFileCrops = form.pages.map((p, i) => p instanceof File ? (pageCrops[i] || null) : null).filter((_, i) => form.pages[i] instanceof File);
      fd.append('crops', JSON.stringify(newFileCrops));
      // 수정 모드: 현재 화면 순서대로 기존 파일명 전달 (신규 파일 위치는 null)
      if (isEdit && editTarget) {
        fd.append('existing_pages', JSON.stringify(pageFilenames));
      }
      const url    = isEdit ? `${API_BASE}/api/contracts/${editTarget.id}` : `${API_BASE}/api/contracts`;
      const method = isEdit ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers, body: fd });
      const data   = await res.json();
      if (!res.ok) { setError(typeof data.detail === 'string' ? data.detail : '업로드 실패'); return; }
      setSuccess('✅ 계약서 업로드 완료');
      setForm({ room_no: '', user_name: '', pages: [] }); setPagePreviews([]); setPageCrops([]); setPageFilenames([]);
      setPagePreviews([]); setPageCrops([]); setPageFilenames([]); setCropIdx(null); setCropTarget(null);
      setShowUpload(false);
      if (clearCache) clearCache();
      fetchContracts();
    } catch { setError('서버 오류'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${id}`, { method: 'DELETE', headers });
      if (!res.ok) { setError('삭제 실패'); return; }
      setSuccess('✅ 삭제 완료');
      setDeleteConfirm(null);
      fetchContracts();
    } catch { setError('서버 오류'); }
  };

  const handleWelcome = async (c) => {
    if (!window.confirm(`${c.room_no} ${c.user_name}에게 입주안내 알림톡을 발송할까요?`)) return;
    setSending(c.id);
    setError(''); setSuccess('');
    try {
      const tokens    = await tokenApi.list(token, c.room_no);
      const doorToken = (tokens || []).find(t => t.is_active && new Date(t.expires_at) >= new Date())?.token || '';
      const roomsRes  = await fetch(`${API_BASE}/api/rooms`, { headers });
      const rooms     = roomsRes.ok ? await roomsRes.json() : [];
      const phone     = rooms.find(r => r.room_no === c.room_no)?.phone || '';
      if (!phone) throw new Error('전화번호가 없습니다');
      await sendWelcomeKakao({ token, phone, doorToken, roomNo: c.room_no, userName: c.user_name, templateId: 'welcome' });
      setSuccess(`✅ ${c.room_no} 입주안내 발송 완료`);
    } catch (e) {
      setError(e.message || '발송 실패');
    } finally { setSending(null); }
  };

  const handleShare = async (c) => {
    try {
      const res  = await fetch(`${API_BASE}/api/contracts/${c.id}/share`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || '링크 생성 실패'); return; }
      const link = `${window.location.origin}/contract-view?token=${data.token}`;
      await navigator.clipboard.writeText(link);
      setSuccess(`✅ 링크 복사 완료 (~${data.expires_at.slice(0, 10)})`);
    } catch { setError('링크 생성 실패'); }
  };

  // 계약서 편집 대상 선택 시 기존 이미지 로드
  const onSelectEditTarget = async (key) => {
    const [roomNo, userName] = key.split('__');
    const c = contracts.find((x) => x.room_no === roomNo && x.user_name === userName);
    if (!c) return;
    setEditTarget(c);
    setShowUpload(true);
    setForm({ room_no: c.room_no, user_name: c.user_name, pages: [] });
    setPageCrops([]);
    // pages 배열 별도 조회
    let pageList = [c.front_path].concat(c.back_path ? [c.back_path] : []);
    try {
      const pr = await fetch(`${API_BASE}/api/contracts/${c.id}/pages`, { headers });
      const pd = await pr.json();
      if (Array.isArray(pd.pages) && pd.pages.length) pageList = pd.pages;
    } catch {}
    try {
      const previews = await Promise.all(
        pageList.filter(Boolean).map(p =>
          fetch(`${API_BASE}/api/contracts/image/${tenantId}/${encodeURIComponent(p)}`, { headers })
            .then(r => r.json()).then(d => d.data).catch(() => null)
        )
      );
      const validPages = pageList.filter(Boolean);
      setPagePreviews(previews.filter(Boolean));
      setPageCrops(previews.map(() => null));
      setPageFilenames(validPages);
    } catch {}
  };

  const resetUploadForm = () => {
    setEditTarget(null);
    setForm({ room_no: '', user_name: '', pages: [] }); setPagePreviews([]); setPageCrops([]); setPageFilenames([]);
    setPagePreviews([]); setPageCrops([]);
    setError(''); setSuccess('');
  };

  // 호실+이름 조합별 활성 여부 (고지서 탭 기준 = rooms is_active)
  const activeKeySet = new Set(
    rooms.filter(r => r.is_active !== false).map(r => `${r.room_no}__${r.user_name}`)
  );
  const floors = [...new Set(rooms.map(r => String(r.room_no)[0]))].sort();

  // 필터 적용된 계약서 목록
  const filteredContracts = contracts.filter(c => {
    const room = String(c.room_no);
    const key  = `${c.room_no}__${c.user_name}`;
    if (filterType === 'all')      return activeKeySet.has(key);
    if (filterType === 'inactive') return !activeKeySet.has(key);
    // 층 필터: 해당 층의 활성 세대만
    return activeKeySet.has(key) && room[0] === filterType;
  });

  const grouped = filteredContracts.reduce((acc, c) => {
    const key = `${c.room_no}__${c.user_name}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  // 수정 탭용: 필터와 무관하게 전체 계약서 기준
  const allGrouped = contracts.reduce((acc, c) => {
    const key = `${c.room_no}__${c.user_name}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  // 뷰어 탭 - pages 배열 기반
  const viewerAllPages = (() => {
    if (!viewer) return [];
    const contracts = viewer.roomContracts || [viewer];
    const pages = [];
    contracts.forEach((c, ci) => {
      const ps = c.pages || ([c.front_path].concat(c.back_path ? [c.back_path] : []));
      ps.forEach((p, pi) => {
        if (p) pages.push({ path: p, label: contracts.length > 1 ? `${ci+1}-${pi+1}` : `${pi+1}` });
      });
    });
    return pages;
  })();
  const viewerImgPath = viewerAllPages[parseInt(viewerTab) || 0]?.path || null;

  return (
    <div style={S.page}>
      {/* 상단 버튼 */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
        <button onClick={() => { fetchContracts(); setError(''); setSuccess(''); }} style={S.btn('#10b981')}>새로고침</button>
        <button onClick={() => { setShowUpload(true); setContractMode('edit'); resetUploadForm(); }} style={S.btn('#4f46e5')}>📄 관리</button>
      </div>

      {error   && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px', borderRadius: '10px', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}
      {success && <div style={{ background: '#dcfce7', color: '#16a34a', padding: '10px', borderRadius: '10px', marginBottom: '10px', fontSize: '13px' }}>{success}</div>}

      {/* 관리 바텀시트 */}
      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => { setShowUpload(false); setEditTarget(null); }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: '500px', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>📄 계약서 관리</div>
              <button onClick={() => { setShowUpload(false); setEditTarget(null); }}
                style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>

            {/* 서브탭 */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              {[['edit', '✏️ 수정'], ['new', '➕ 신규']].map(([mode, label]) => (
                <button key={mode}
                  onClick={() => { setContractMode(mode); resetUploadForm(); }}
                  style={{ flex: 1, padding: '9px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
                           backgroundColor: contractMode === mode ? '#4f46e5' : '#f1f5f9',
                           color:           contractMode === mode ? '#fff'    : '#64748b' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* 조회/수정 모드 */}
            {contractMode === 'edit' && (
              <>
                <div style={S.label}>{L.room_no} 선택</div>
                <select onChange={(e) => onSelectEditTarget(e.target.value)}
                  style={{ ...S.input, backgroundColor: '#fff', marginBottom: '12px' }}>
                  <option value="">호실 선택</option>
                  {Object.keys(allGrouped).map((k) => <option key={k} value={k}>{k.split('__')[0]}호 {k.split('__')[1]}</option>)}
                </select>
                {editTarget && (
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                    현재: {editTarget.user_name}
                  </div>
                )}
              </>
            )}

            {/* 신규 등록 모드 */}
            {contractMode === 'new' && (<div style={{ fontSize:'11px', color:'#6b7280', marginBottom:'8px' }}>신규 입주자 계약서 등록</div>)}  {contractMode === 'new' && (
              <>
                <div style={S.label}>{L.room_no}</div>
                <select onChange={(e) => {
                    const room = rooms.find((r) => r.room_no === e.target.value);
                    setForm((f) => ({ ...f, room_no: e.target.value, user_name: room?.user_name || '' }));
                  }}
                  style={{ ...S.input, backgroundColor: '#fff', marginBottom: '10px' }}>
                  <option value="">{L.room_no} 선택</option>
                  {rooms.map((r) => <option key={r.room_no} value={r.room_no}>{r.room_no}</option>)}
                </select>
              </>
            )}

            {/* 폼 공통 (이름 + 앞면 + 뒷면) */}
            {(contractMode === 'new' || (contractMode === 'edit' && editTarget)) && (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <div style={S.label}>입주자 이름</div>
                  <input value={form.user_name} onChange={(e) => setForm((f) => ({ ...f, user_name: e.target.value }))}
                    placeholder="이름 입력" style={S.input} />
                </div>

                {/* ── 공통 업로드 박스 2개 ── */}
                {pagePreviews.map((preview, i) => {
                  const isDragging = dragIdx === i;
                  let order = i;
                  if (dragIdx !== null) {
                    const targetIdx = Math.max(0, Math.min(pagePreviews.length - 1, dragIdx + Math.round(dragY / ITEM_H)));
                    if (i === dragIdx) order = targetIdx;
                    else if (dragIdx < targetIdx && i > dragIdx && i <= targetIdx) order = i - 1;
                    else if (dragIdx > targetIdx && i < dragIdx && i >= targetIdx) order = i + 1;
                  }
                  const onDragStart = (e) => {
                    if (e.target.closest('button')) return;
                    dragStartYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
                    setDragIdx(i); setDragY(0);
                  };
                  const onDragMove = (e) => {
                    if (dragIdx === null) return;
                    const y = e.touches ? e.touches[0].clientY : e.clientY;
                    setDragY(y - dragStartYRef.current);
                  };
                  const onDragEnd = () => {
                    if (dragIdx === null) return;
                    const targetIdx = Math.max(0, Math.min(pagePreviews.length - 1, dragIdx + Math.round(dragY / ITEM_H)));
                    if (targetIdx !== dragIdx) {
                      const reorder = arr => { const a=[...arr]; const [item]=a.splice(dragIdx,1); a.splice(targetIdx,0,item); return a; };
                      setPagePreviews(prev => reorder(prev));
                      setPageCrops(prev => reorder(prev));
                      setPageFilenames(prev => reorder(prev));
                      setForm(f => ({...f, pages: reorder(f.pages||[])}));
                    }
                    setDragIdx(null); setDragY(0);
                  };
                  return (
                  <div key={i}
                    onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
                    onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
                    style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px',
                             backgroundColor: isDragging ? '#e0e7ff' : '#f8fafc', borderRadius:'10px', padding:'8px 12px',
                             transform: isDragging ? `translateY(${dragY}px)` : `translateY(${(order - i) * (ITEM_H)}px)`,
                             transition: isDragging ? 'none' : 'transform 0.15s ease',
                             position: 'relative', zIndex: isDragging ? 10 : 1,
                             boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                             touchAction: 'none', cursor: 'grab', userSelect: 'none' }}>
                    <span style={{ fontSize:'14px', color:'#94a3b8' }}>☰</span>
                    <span style={{ fontSize:'12px' }}>📄</span>
                    <div style={{ flex:1, fontSize:'12px', color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {(form.pages||[])[i]?.name || `이미지 ${i+1}`}{pageCrops[i] ? ' ✅' : ''}
                    </div>
                    <button onClick={() => setCropIdx(i)}
                      style={{ padding:'4px 8px', backgroundColor:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>✂️</button>
                    <button onClick={() => { setPagePreviews(p=>p.filter((_,j)=>j!==i)); setForm(f=>({...f,pages:(f.pages||[]).filter((_,j)=>j!==i)})); setPageCrops(c=>c.filter((_,j)=>j!==i)); setPageFilenames(fn=>fn.filter((_,j)=>j!==i)); }}
                      style={{ padding:'4px 8px', backgroundColor:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>✕</button>
                  </div>
                  );
                })}
                <ImageUploadBox
                  label={pagePreviews.length ? `페이지 ${pagePreviews.length + 1} 추가` : "계약서 이미지"}
                  labelNote={editTarget ? '(변경 시만 선택)' : pagePreviews.length ? "" : "(여러 장 추가 가능)"}
                  icon="📄"
                  preview={null}
                  hasCrop={false}
                  onFileChange={(file, dataUrl) => {
                    setForm(f=>({...f, pages:[...(f.pages||[]), file]}));
                    setPagePreviews(p=>[...p, dataUrl]);
                    setPageCrops(c=>[...c, null]);
                    setPageFilenames(fn=>[...fn, null]);
                  }}
                  onCropClick={() => {}}
                  style={{ marginBottom: '14px' }}
                />

                <div style={{ display: 'flex', gap: '8px' }}>
                  {contractMode === 'edit' && editTarget && (
                    <button onClick={() => { if (window.confirm(`${editTarget.room_no} ${editTarget.user_name} 계약서를 삭제할까요?`)) { handleDelete(editTarget.id); setShowUpload(false); setEditTarget(null); } }}
                      style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: '#fef2f2', color: '#dc2626', fontWeight: 'bold', fontSize: '13px' }}>
                      🗑 삭제
                    </button>
                  )}
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !form.room_no || !form.user_name || (contractMode === 'new' && !form.pages.length)}
                    style={{ flex: 2, padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                             backgroundColor: (uploading || !form.room_no || !form.user_name || (contractMode === 'new' && !form.pages.length)) ? '#9ca3af' : '#4f46e5',
                             color: '#fff' }}>
                    {uploading ? '저장 중...' : (contractMode === 'edit' && editTarget ? '✅ 수정 저장' : '✅ 등록')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 크롭 툴 — 공통 컴포넌트 사용 */}
      {cropIdx !== null && pagePreviews[cropIdx] && createPortal(
        <CropTool
          src={pagePreviews[cropIdx]}
          onDone={(box) => { setPageCrops(c=>c.map((cc,i)=>i===cropIdx?box:cc)); setCropIdx(null); }}
          onCancel={() => setCropIdx(null)}
        />,
        document.body
      )}

      {/* 세대 필터 */}
      <div style={{ marginBottom: '12px' }}>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          style={{ ...S.input, backgroundColor: '#fff' }}>
          <option value="all">전체세대</option>
          <option value="inactive">퇴거세대</option>
          {floors.map(f => <option key={f} value={f}>{f}층</option>)}
        </select>
      </div>

      {/* 목록 */}
      {loading && contracts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '30px' }}>로딩 중...</div>
      ) : contracts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '30px' }}>등록된 계약서 없음</div>
      ) : (
        Object.entries(grouped).map(([key, items]) => {
          const [roomNo] = key.split('__');
          const latest   = items[0];
          const shareTarget = items.find(c => c.back_path) || latest;
          return (
          <div key={key} style={S.card}>
            <div style={S.row}>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1f2937' }}>{roomNo}호 {latest.user_name}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => openViewer(latest)} style={S.btn('#4f46e5')}>보기</button>
                <button onClick={() => handleShare(shareTarget)} style={S.btn('#059669')}>🔗 공유</button>
                <button onClick={() => handleWelcome(latest)} disabled={sending===latest.id} style={S.btn(sending===latest.id?'#9ca3af':'#f59e0b')}>✉️ {sending===latest.id?'발송중…':'입주안내'}</button>
              </div>
            </div>
            {deleteConfirm === latest.id && (
              <div style={{ margin: '0 15px 12px', padding: '12px', backgroundColor: '#fff1f2', borderRadius: '10px', border: '1px solid #fecdd3' }}>
                <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 'bold', marginBottom: '4px' }}>⚠️ 삭제 확인</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>{latest.room_no} {latest.user_name} 계약서를 삭제합니다.</div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => handleDelete(latest.id)} style={{ ...S.btn('#dc2626'), flex: 1 }}>삭제</button>
                  <button onClick={() => setDeleteConfirm(null)}
                    style={{ flex: 1, padding: '8px', backgroundColor: '#e5e7eb', color: '#4b5563', border: 'none', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>취소</button>
                </div>
              </div>
            )}
          </div>
          );
        })
      )}

      {/* 이미지 뷰어 — 공통 컴포넌트 사용 (탭바 포함) */}
      {viewer && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.9)', zIndex:1000, display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', backgroundColor:'#1f2937', flexShrink:0 }}>
            <div>
              <span style={{ color:'#fff', fontWeight:'bold', fontSize:'14px' }}>{viewer.room_no}호 {viewer.user_name}</span>
              <span style={{ color:'#9ca3af', fontSize:'11px', marginLeft:'8px' }}>{viewerAllPages.length}페이지</span>
            </div>
            <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
              <button onClick={() => downloadAllPdf(viewerAllPages.map(p => imgCache[p.path]).filter(Boolean), `${viewer.room_no}_${viewer.user_name}_계약서`)}
                style={{ padding:'6px 12px', backgroundColor:'#dc2626', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>
                📄 PDF
              </button>
              <button onClick={() => setViewer(null)}
                style={{ background:'none', border:'none', color:'#fff', fontSize:'20px', cursor:'pointer' }}>✕</button>
            </div>
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'12px', display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
            {viewerAllPages.map((p, i) => (
              <div key={i} style={{ width:'100%', maxWidth:'800px' }}>
                {imgCache[p.path] ? (
                  <img src={imgCache[p.path]} alt={`p${i+1}`} draggable={false}
                    onContextMenu={e => e.preventDefault()}
                    style={{ width:'100%', borderRadius:'8px', display:'block', userSelect:'none', WebkitUserSelect:'none' }} />
                ) : (
                  <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'200px', color:'#9ca3af', fontSize:'13px' }}>로딩 중...</div>
                )}
                {i < viewerAllPages.length - 1 && <div style={{ height:'1px', backgroundColor:'#374151', margin:'12px 0' }} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
